<script setup lang="ts">
/**
 * **顶栏**：品牌区 + 菜单栏 + 临时提示条。
 *
 * 从 `Playground.vue` 抽出来的第一个**视图**块（之前的抽取都是逻辑）。
 * 原样照搬的那段里，这几样都来自宿主作用域，现在改成显式 props ——
 * 输入只有 5 个，接口很窄：
 *
 *   `menus` / `openMenu`      菜单栏分组与点击（菜单模型仍归 `useMenus`）
 *   `notice` / `actions`      提示条文字与临时动作按钮（如「撤销」）
 *   `editorName` / `version`  品牌区（宿主来自 defineProps，集成方可覆盖）
 *
 * ⚠️ `.pg__spacer` 的样式在这里**重复**了一份：它在宿主的面包屑里也用到，
 * 而 scoped 样式不会跨组件。三行 CSS，比为此引一层全局样式划算。
 *
 * 另外这里放**工作区标签栏**（对应 Blender 顶栏那排
 * `Layout / Modeling / Sculpting`）：它是「换整套窗口排布」的入口，
 * 比埋进「窗口 → 布局」菜单少一步，而且**当前在哪一个**一眼可见。
 */
import type { NoticeAction } from '@/playground/composables/useNotice'

/** 菜单栏的一个分组（只需要 id / label） */
interface MenuDef {
  id: string
  label: string
}

defineProps<{
  menus: readonly MenuDef[]
  editorName: string
  version: string
  notice: string
  /*
   * ⚠️ 这里收窄成 `{ label }`：宿主那个数组的类型是 `NoticeAction`，
   * 而菜单项的 `MenuItem` 要求 `id` —— 用 MenuItem 会不兼容。
   * 组件只渲染 `label`，就该只要求 `label`。
   */
  actions: readonly Pick<NoticeAction, 'label'>[]
  /** 工作区清单（自存的 + 内置），顺序就是标签顺序 */
  workspaces: readonly { id: string; label: string; hint?: string; removable: boolean }[]
  /** 当前处在哪个工作区；`null` 表示当前排布不属于任何一个 */
  activeWorkspaceId: string | null
}>()


const emit = defineEmits<{
  /**
   * 点某个菜单栏分组。
   *
   * ⚠️ `id` 声明成 `string` 而不是联合类型：宿主的 `openBarMenu`
   * 收的是它自己的 `BarMenuId` 联合 —— 参数类型必须比它**宽**才兼容。
   */
  openMenu: [event: MouseEvent, id: string]
  /** 点提示条上的临时动作按钮（宿主自己知道它是什么类型） */
  runAction: [action: unknown]
  /** 切到某个工作区 */
  pickWorkspace: [id: string]
  /** 把当前排布存成新的工作区 */
  saveWorkspace: []
  /** 删掉一个自存的工作区 */
  removeWorkspace: [id: string]
}>()
</script>

<template>
<header class="pg__bar">
  <!--
    左边是**品牌区**：只有编辑器名称（来自 prop，集成方可以换成自己的）。

    ⚠️ 版本号**去掉了** —— 底部状态条的右下角已经有一个，
    实测反馈是「窗口整体左上角的版本号可以去掉了，因为右下角有版本号了」。
    同一个信息一屏里出现两次，第二次就是噪音。

    ⚠️ `version` 这个 prop **留着**：底部状态条就是读它那个值渲染的
    （`Playground.vue` 的 `.pg__statusbar-version`），只是不再在这里显示。
  -->
  <strong class="pg__brand">{{ editorName }}</strong>

  <!--
    菜单栏：按用途分组。
    原来是一个「菜单 ▾」全部塞在一起，现在拆成 文件 / 编辑 / 视图 / 窗口。
  -->
  <nav class="pg__menubar">
    <button
      v-for="menuDef in menus"
      :key="menuDef.id"
      class="pg__menu-btn"
      type="button"
      @click="emit('openMenu', $event, menuDef.id)"
    >
      {{ menuDef.label }}
    </button>
  </nav>

  <!--
    ⚠️ 这里原来有 `statusText`（最顶上那行错误/提示）。
    它和输出窗口是**同一个信息的两个出口**，而且提示条几秒就没了、
    长得又像报错。现在统一交给输出窗口。
  -->
  <span class="pg__spacer" />

  <!--
    工作区标签栏 —— 学 Blender 顶栏那排 Layout / Modeling / Sculpting。
    点一下换**整套**窗口排布；`activeWorkspaceId` 决定谁被点亮。
  -->
  <nav class="pg__workspaces" aria-label="工作区">
    <button
      v-for="ws in workspaces"
      :key="ws.id"
      class="pg__ws"
      :class="{ 'is-on': ws.id === activeWorkspaceId }"
      type="button"
      :title="ws.hint ? `${ws.label} —— ${ws.hint}` : ws.label"
      :aria-pressed="ws.id === activeWorkspaceId"
      @click="emit('pickWorkspace', ws.id)"
    >
      {{ ws.label }}
      <!--
        删除只在**自存的**工作区上出现，而且只在它被选中时才显示 ——
        一排 × 会把标签栏变成一片叉，误删的代价又很高。
      -->
      <span
        v-if="ws.removable && ws.id === activeWorkspaceId"
        class="pg__ws-remove"
        role="button"
        :aria-label="`删除工作区 ${ws.label}`"
        :title="`删除工作区「${ws.label}」`"
        @click.stop="emit('removeWorkspace', ws.id)"
        >×</span
      >
    </button>

    <!-- 结构不属于任何一个工作区时，给一个「存下来」的出口 -->
    <button
      v-if="activeWorkspaceId === null"
      class="pg__ws pg__ws--save"
      type="button"
      title="把当前排布存成一个工作区"
      @click="emit('saveWorkspace')"
    >
      ＋ 存为工作区
    </button>
  </nav>

  <!-- 提示条上只剩「可撤销」这种**临时动作**（比如断开连线的撤销） -->
  <span v-if="actions.length" class="pg__status">{{ notice }}</span>
  <button
    v-for="action in actions"
    :key="action.label"
    class="pg__status-action"
    type="button"
    @click="emit('runAction', action)"
  >
    {{ action.label }}
  </button>
</header>
</template>

<style scoped>
/*
 * ── 工作区标签栏 ──────────────────────────────────────────────
 *
 * ⚠️ 它会**横向滚**而不是换行：顶栏高度是固定的，
 * 换行会把下面的画布顶下去（而且每多一个工作区就顶一次）。
 * 滚动条本身隐藏 —— 一排标签下面挂一条灰杠很难看，
 * 手机上还能横向滑。
 */
.pg__workspaces {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  /* 触摸端横向滑动很自然，别让浏览器把它当成页面滚动 */
  touch-action: pan-x;
}

.pg__workspaces::-webkit-scrollbar {
  height: 0;
}

.pg__ws {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  height: 22px;
  padding: 0 9px;
  border: 1px solid transparent;
  border-radius: 11px;
  background: transparent;
  color: var(--np-panel-dim, #9a9a9a);
  font-size: 11px;
  white-space: nowrap;
  cursor: pointer;
  touch-action: manipulation;
}

.pg__ws:hover {
  color: var(--np-panel-text, #d8d8d8);
  background: var(--np-surface-3, rgba(255, 255, 255, 0.08));
}

/*
 * 当前工作区：实心填充 + 强调色。
 * ⚠️ 用 `is-on`（soft 底 + 强调色文字）而不是 `is-active`（实心强调色）——
 * 实心填充是「工具/模式」的语义（全局最多一个且是用户选的模式），
 * 而这里是「你在哪一份排布里」，属于位置指示。
 */
.pg__ws.is-on {
  border-color: var(--np-accent, #f0a04b);
  background: var(--np-accent-soft, rgba(240, 160, 75, 0.18));
  color: var(--np-accent, #f0a04b);
}

/* 删除叉：小、只在选中的那一项上出现，悬停才实心 */
.pg__ws-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  font-size: 12px;
  line-height: 1;
  opacity: var(--np-opacity-soft);
}

.pg__ws-remove:hover {
  /* 危险态的弱底 + 前景，走面板令牌（原来是一对写死值） */
  background: var(--np-panel-error-chip);
  color: var(--np-panel-error);
  opacity: var(--np-opacity-shown);
}

/* 「存为工作区」：虚线边，一眼看出它和别的标签不是一类 */
.pg__ws--save {
  border-style: dashed;
  border-color: var(--np-border-strong, rgba(255, 255, 255, 0.2));
}

/*
 * ⚠️ 窄屏**隐藏标签栏**，改用「视图 → 布局」菜单。
 *
 * 用户的要求：「预设布局也在宽度变化的时候隐藏，在菜单项使用，
 * 菜单项永远在」。标签栏是**便捷**入口（少一次点击），
 * 菜单项是**保底**入口（永远在）—— 宽度不够时保便捷、留保底。
 *
 * ⚠️ 用媒体查询而不是溢出测量：标签栏是横向滚动的一排，
 * 「放不下」不会像按钮组那样产生可测的溢出（它滚一下就能看全）。
 * 这里要表达的是「屏幕太窄，别占那一行」，本来就是个宽度判断。
 */
/*
 * 窄屏收起工作区标签栏 —— 它一排能放七八个（7 份内置预设 + 用户存的），
 * 手机上顶栏会被它吃掉一整行，而画布才是主角。
 */
@media (max-width: 860px) {
  .pg__workspaces {
    display: none;
  }
}

/*
 * ⚠️ **触摸设备也收起**，不只看宽度。
 *
 * 手机上横向拿着一台「宽度 > 860px」的设备（横屏手机、折叠屏展开、
 * 小平板）时，上面那条按宽度判断的规则**不生效** —— 于是顶栏右侧
 * 又冒出那一排预设布局按钮（实测反馈：「移动端应该减少右上角的
 * 预设布局切换按钮」）。
 *
 * 判据用 `(pointer: coarse)` 而不是 `any-pointer`：触屏笔记本的
 * **主**指针是触控板（fine），它不该被卷进来。
 *
 * ⚠️ 收起**不是删掉**：同一份工作区清单还在「视图 → 布局」菜单里
 * （见 `menus/barSections.ts` 的 workspaces 那一段），
 * 而且那是**同一份数据**（`ctx.workspaces`），不会分叉。
 */
@media (pointer: coarse) {
  .pg__workspaces {
    display: none;
  }
}

/* ── 菜单栏 ── */

.pg__menubar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 10px;
  /*
   * ⚠️ 这一行是「品牌名 + 版本号 + 4 个菜单按钮」，窄屏放不下。
   *
   * 历史上有过两种错误修法，都踩过：
   *   ① 让它横向滚动 —— 滚动条被 `scrollbar-width: none` 藏了，
   *      用户看到的是「菜单不见了」而不是「可以滚」；
   *   ② 只在 ≤560px 换行 —— 561–720px 那段落回 ①，同一个坑换个宽度复发。
   *
   * 现在的做法：**基础规则就允许换行**，不再依赖任何断点。
   * 宽屏下四个按钮本来就在一行，换行规则不生效、观感不变；
   * 窄屏（或集成方把 `editorName` 换成长名字）时自动折到第二行。
   * 顶栏高度相应改成 `min-height`（见 `.pg__bar`），不会把内容切掉。
   */
  flex-wrap: wrap;
  min-width: 0;
  overflow: visible;
}

.pg__menu-btn {
  padding: 4px 10px;
  border: 0;
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-text);
  font: inherit;
  font-size: 11.5px;
  cursor: pointer;
}

.pg__menu-btn:hover {
  background: var(--np-surface-3);
  color: var(--np-panel-text);
}

.pg__status {
  color: var(--np-panel-dim);
  font-size: 11px;
}

/* 提示条上的动作按钮（比如断开连线的「撤销」） */
.pg__status-action {
  padding: 1px 8px;
  border: 1px solid var(--np-accent);
  border-radius: 4px;
  background: transparent;
  color: var(--np-accent);
  font: inherit;
  font-size: 11px;
  cursor: pointer;
}

.pg__status-action:hover {
  background: var(--np-accent);
  /* 压在实心强调色上的字 —— 走全局令牌，浅色主题才不会得到"深字压深色" */
  color: var(--np-on-accent);
}

/* ── 品牌区 ──────────────────────────────────────────────────────── */
/* ⚠️ 基准与浅色覆盖都从宿主搬进来：组件化后宿主的 scoped 样式够不到这里 */

.pg__brand {
  font-size: 12.5px;
  letter-spacing: 0.2px;
  /* 窄屏别把品牌名压成两行 —— 宁可让菜单栏横向滚动 */
  flex: 0 0 auto;
  white-space: nowrap;
}

/* 版本号：品牌区的一部分，但视觉上要弱 */
/*
 * 窄屏：让位顺序 —— 版本号先走（它最不重要），品牌名收窄到能认出来为止。
 *
 * ⚠️ 这里**不再**设 `height: auto` / `flex-wrap` ——
 * 那两个已经上移到基础规则（`.pg__bar` 的 `min-height`、
 * `.pg__menubar` 的 `flex-wrap`），所以 561–720px 那段缝隙不存在了。
 * 留在这里的只有「谁先让位」这一件事。
 *
 * 断点选 720px：「节点设计器 v0.1.0 文件 编辑 视图 工具」大约需要这么宽。
 */
@media (max-width: 720px) {
  /*
   * ⚠️ 这里原来还有一条 `.pg__version { display: none }`。
   * 版本号从顶栏挪到底部状态条之后它没有元素了，一起删。
   */
  .pg__brand {
    font-size: 11.5px;
    flex: 0 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pg__menubar {
    margin-left: 6px;
  }
  .pg__menu-btn {
    padding: 4px 8px;
    font-size: 11px;
  }
}

/* 触摸端：顶栏和按钮都按「可点」的下限放大 */
@media (pointer: coarse) {
  .pg__bar {
    min-height: 44px;
  }
  .pg__menu-btn {
    min-height: 36px;
    padding: 6px 12px;
    font-size: 12px;
  }
}

/* ⚠️ 和宿主重复一份：面包屑也在用（scoped 样式不跨组件） */
.pg__spacer {
  flex: 1 1 auto;
}

/*
 * ── 顶栏本体 ──────────────────────────────────────────────────────
 *
 * ⚠️ 这两条原来是写在宿主里的（`.pg__bar` / `.pg.np-theme-light .pg__bar`）。
 * 组件化之后宿主的样式**匹配不到这里**：DOM 里 `.pg` 与 `.pg__bar` 之间
 * 还隔着一层组件根节点，两边的 scoped 属性不同 ——
 * 于是浅色主题下顶栏还是深色底。
 * 所以基准样式和浅色覆盖**都必须在本组件里各留一份**。
 */
.pg__bar {
  display: flex;
  align-items: center;
  gap: 9px;
  /*
   * ⚠️ **允许换行**（不再依赖断点）。
   * 顶栏内容 = 品牌 + 版本 + 菜单栏 + 状态 + 动作按钮，窄屏一定放不下。
   * 固定 `nowrap` 会把后面的内容挤出可视区（「顶栏看不到菜单」）。
   * 换行后高度由 `min-height` 兜底，所以不会被切。
   */
  flex-wrap: wrap;
  row-gap: 2px;
  /*
   * ★ 防御：顶栏必须在所有画布层之上。
   * 之前出现过「顶栏看得见但点不到」——多半是某个覆盖层（面板容器 / 浮层）
   * 压在它上面吃掉了点击。给一个明确的层级，不再靠文档顺序碰运气。
   */
  position: relative;
  z-index: var(--np-z-chrome, 30);
  /*
   * ⚠️ `flex: 0 0 auto` + `min-width: 0`：
   * 顶栏不该被下面的画布压扁，也不该因为内容过宽而把整页撑出横向滚动。
   */
  flex: 0 0 auto;
  min-width: 0;
  /*
   * ⚠️ `min-height` 而不是 `height`：
   * 菜单栏在窄屏会换行（见 `.pg__menubar`），固定高度会把第二行切掉。
   * 宽屏下内容和 40px 差不多高，所以观感不变。
   */
  min-height: 40px;
  padding: 4px 14px;
  border-bottom: 1px solid var(--np-border-bar);
  background: var(--np-surface-bar);
}

/*
 * 浅色主题：这两条原来是写在宿主的 `.pg.np-theme-light` 段落里。
 * ⚠️ 搬进子组件时**必须一起搬**，否则 scoped 属性选择器会把它留给宿主，
 * 而宿主的样式够不到子组件内部 —— 浅色主题下菜单文字颜色就失效了。
 *
 * ⚠️ 这几条放在**非 scoped** 的 `<style>` 里：scoped 下即使加 `:global()`
 * 也会把「祖先 + 内层」的复合选择器拆坏（实测内层选择器会丢，规则变成
 * 裸的 `.pg.np-theme-light`，反而污染全局）。类名本来就带 `pg__` 前缀，非 scoped 没有冲突风险。
 */
</style>


<!--
  浅色主题的覆盖。

  ⚠️ 只剩下面这**一条**了。
  顶栏底色 / 边框 / 版本徽标底色 / 菜单文字色全部已由 theme.css 的
  `:root.np-theme-light` 令牌翻转接管
  （`--np-surface-bar` / `--np-border-bar` / `--np-panel-chip-bg` /
  `--np-panel-text`），不需要在这再抄一遍。

  留下的这条是**对比度微调**（`--np-panel-dim` 的浅色值 `#5a5a5a`
  压在这个底色上偏淡），不是主题翻转，所以必须留在本地。

  ⚠️ **故意不加 scoped**：`.np-theme-light` 挂在设计器根节点上（组件之外），
  scoped 会让 `[data-v-…]` 加在内层元素上、外层根本匹配不到；
  而 `:global()` 在这里会把复合选择器拆坏。类名都带 `pg__` 前缀，全局不冲突。
-->
<style>
/*
 * ⚠️ 这里原来有一条 `.pg.np-theme-light .pg__version`（浅色主题下把版本号
 * 压暗）。版本号搬到底部状态条之后顶栏没有这个元素了，已删；
 * 底部那一条的颜色由 `--np-panel-mute` 管，跟着主题走。
 */
</style>
